import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type CardLocation from '@alltrails/amplitude/enums/CardLocation';
import logHikeEventCreated from '@alltrails/amplitude/events/logHikeEventCreated';
import logHikeEventCreateStarted from '@alltrails/amplitude/events/logHikeEventCreateStarted';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import type { AuthedEvent, EventMapType } from '../types';
import eventCreatedParam from '../utils/eventCreatedParam';
import getEventMapAnalyticsIds from '../utils/getEventMapAnalyticsIds';

const CreateEventModal = dynamic(() => import('../components/CreateEventModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type useEventCreationArgs = {
  entryPoint?: CardLocation;
  mapId: number;
  mapType: EventMapType;
};

const useCreateEventCta = ({ entryPoint, mapId, mapType }: useEventCreationArgs) => {
  const intl = useIntl();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const isHikeTogetherV2 = useExperiments()['web-disco-rm-hike-together-v2']?.value === 'on';

  const newTabRef = useRef<Window | null>(null);
  const [isCreateEventModalOpen, setIsCreateEventModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  useEffect(() => {
    if (isCreateEventModalOpen) {
      logHikeEventCreateStarted({ entry_point: entryPoint, ...getEventMapAnalyticsIds(mapType, mapId) });
    }
  }, [isCreateEventModalOpen, entryPoint, mapType, mapId]);

  const onClick = useCallback(() => {
    if (user) {
      setIsCreateEventModalOpen(true);
    } else {
      setIsSignupModalOpen(true);
    }
  }, [user]);

  // Mobile browsers block `window.open` once the create request has been awaited, so claim the tab
  // while the submit click is still the active user gesture and point it at the event afterwards.
  const onSubmitStart = useCallback(() => {
    if (isHikeTogetherV2) {
      newTabRef.current = window.open('', '_blank');
    }
  }, [isHikeTogetherV2]);

  const onSubmitError = useCallback(() => {
    newTabRef.current?.close();
    newTabRef.current = null;
  }, []);

  const onEventCreation = useCallback(
    (event: AuthedEvent) => {
      setIsCreateEventModalOpen(false);
      logHikeEventCreated({
        entry_point: entryPoint,
        event_id: event.id,
        has_note: Boolean(event.notes),
        ...getEventMapAnalyticsIds(mapType, mapId)
      });
      const eventSlug = wrapUrlSafe(`/event/${event.id}`, languageRegionCode);
      if (isHikeTogetherV2) {
        const eventUrl = `${eventSlug}?${eventCreatedParam}=true`;
        const newTab = newTabRef.current;
        newTabRef.current = null;
        if (newTab) {
          newTab.location.replace(eventUrl);
        } else {
          window.open(eventUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        window.location.assign(eventSlug);
      }
    },
    [entryPoint, isHikeTogetherV2, languageRegionCode, mapId, mapType]
  );

  const modals = useMemo(
    () => (
      <>
        {isSignupModalOpen && (
          <SignUpModal
            isOpen={isSignupModalOpen}
            closeModal={() => setIsSignupModalOpen(false)}
            heading={isHikeTogetherV2 ? intl.formatMessage({ defaultMessage: 'To create an invite, log in or sign up for free' }) : undefined}
            deeplinkData={mapType === 'T' ? { pageId: mapId, pageType: 'trail' } : undefined}
            onSuccess={() => {
              setIsSignupModalOpen(false);
              setIsCreateEventModalOpen(true);
            }}
            trigger="create invite"
            upsellTrigger="create invite"
          />
        )}
        {isCreateEventModalOpen && (
          <CreateEventModal
            mapId={mapId}
            mapType={mapType}
            onCloseRequest={() => setIsCreateEventModalOpen(false)}
            onSubmitError={onSubmitError}
            onSubmitStart={onSubmitStart}
            onSuccess={onEventCreation}
          />
        )}
      </>
    ),
    [isSignupModalOpen, isCreateEventModalOpen, mapId, mapType, onEventCreation, intl, isHikeTogetherV2, onSubmitError, onSubmitStart]
  );

  return useMemo(() => ({ modals, onClick }), [modals, onClick]);
};

export default useCreateEventCta;
